import { createElement, useEffect, useState } from 'react';
import { Container, Grid2 as Grid, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { WButton } from '@wareclouds/wareclouds-ui';
import { useTranslation } from 'react-i18next';
import { useGetPaymentPeriods } from '../../hooks';
import { Box } from '@mui/system';
import {
  PeriodSelector,
  SemiMonthlySummaryPageSkeleton,
  ServiceBadge,
} from '../components';
import { useGetServiceTabItems } from '../../hooks';
import { Tabs } from '../../../common/components';
import { DashboardEmptyState } from '../components/states';
import env from '../../../../config/env';

export default function SemiMonthlySummaryPage() {
  const { t } = useTranslation(['title', 'description', 'form', 'button']);
  const [period, setPeriod] = useState(null);

  const {
    tabsItems,
    isLoading: isLoadingTabs,
    paymentClouderServices: tabsPaymentClouderServices,
    handleActiveTabChange,
    activeTab,
  } = useGetServiceTabItems({
    period,
  });

  const {
    data: paymentPeriods,
    isLoading: isLoadingPaymentPeriods,
    isFetching: isFetchingPaymentPeriods,
    isSuccess: isPaymentPeriodsSuccess,
  } = useGetPaymentPeriods({
    service: activeTab || tabsPaymentClouderServices?.[0],
  });

  const hasMultipleServices = tabsPaymentClouderServices?.length > 1;
  const hasServices = tabsPaymentClouderServices?.length > 0;

  const handlePeriodChange = (value) => {
    setPeriod(value);
  };

  useEffect(() => {
    if (isPaymentPeriodsSuccess) {
      setPeriod(paymentPeriods[0] || null);
    }
  }, [isPaymentPeriodsSuccess, paymentPeriods]);

  if (isLoadingTabs) {
    return <SemiMonthlySummaryPageSkeleton />;
  }

  return (
    <Container maxWidth="xl" sx={{ my: 4 }}>
      <Grid container spacing={3} sx={{ mt: 2, mb: 4 }}>
        <Grid size={{ xs: 12 }}>
          <Box component="header">
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: !hasMultipleServices ? 'auto' : 12 }}>
                <Typography
                  sx={{ typography: { xs: 'h5', md: 'h4' } }}
                  component="h1"
                >
                  {t('payments.semi_monthly_summary', { ns: 'title' })}
                </Typography>
              </Grid>

              {!hasMultipleServices && hasServices && (
                <Grid size={{ xs: 'auto' }}>
                  <ServiceBadge
                    label={t(
                      `services.${tabsItems[0]?.serviceName?.toLocaleLowerCase()}`,
                      {
                        ns: 'description',
                      }
                    )}
                  />
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Typography variant="body1" color="textSecondary">
                  {t('payments.semi_monthly_summary', {
                    ns: 'description',
                  })}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Box component="main">
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }} container justifyContent="space-between">
                <Grid size={{ xs: 12, md: 4 }}>
                  <PeriodSelector
                    options={paymentPeriods}
                    onChange={handlePeriodChange}
                    isLoading={
                      isLoadingPaymentPeriods || isFetchingPaymentPeriods
                    }
                    period={period}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 'auto' }}>
                  <WButton
                    variant="contained"
                    label={t('payments_learn_more', { ns: 'button' })}
                    fullWidth
                    startIcon={<AccessTimeIcon />}
                    component="a"
                    href={env.HOW_TO_LEARN_PAYMENT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                </Grid>

                <Grid
                  container
                  size={{ xs: 12 }}
                  spacing={2}
                  justifyContent="space-between"
                >
                  <Grid size={{ xs: 12 }}>
                    {hasMultipleServices ? (
                      <Tabs
                        tabsItems={tabsItems}
                        handleActiveTabChange={handleActiveTabChange}
                      />
                    ) : (
                      createElement(
                        tabsItems[0]?.component || DashboardEmptyState,
                        {
                          ...(tabsItems[0] || {}),
                        }
                      )
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}
